import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { SlotName } from "../../config/platforms/types";
import type { AdNode } from "../../core/AdNode";
import invariant from "tiny-invariant";
import type { AdEngine } from "../../core/AdEngine";

interface SlotRefresherPluginApi {
  stop: () => void;
  stopSlot: (slotName: SlotName) => void;
}

/**
 * SlotRefresherPlugin is responsible for refreshing ad slots when they become visible.
 * It listens for the `impressionViewable` event and refreshes the ad slot after a certain amount of time.
 * The amount of time is specified by the `icRefreshTimeoutMS` config.
 * The plugin will only request a new ad if the ad slot is refreshable.
 */
export class SlotRefresherPlugin extends AdEnginePlugin<SlotRefresherPluginApi> {
  private active: boolean = true;
  private adEngine?: AdEngine;

  constructor(private readonly config: { icRefreshTimeoutMS?: number } = {}) {
    super();
  }

  /**
   * Adds an event listener to refresh the ad slot when it becomes
   * at least 50% visible in the viewport. The listener is removed
   * once the slot has been refreshed.
   *
   * @param adNode - The ad slot to potentially refresh.
   * @param gptWrapper - The GPT wrapper to manage events and slot requests.
   */
  private refreshWhenBackInViewport(adNode: AdNode, adEngine: AdEngine) {
    const remove = adEngine.gptWrapper.addSlotEventListener(
      adNode.getSlotName(),
      "slotVisibilityChanged",
      (event: googletag.events.SlotVisibilityChangedEvent) => {
        if (event.inViewPercentage >= 50) {
          if (this.active) {
            adEngine.requestRender([adNode]);
          }

          remove();
        }
      },
    );
  }

  /**
   * If the ad slot is currently in viewport, refreshes the ad slot.
   * Otherwise, adds a listener to refresh the ad slot when it becomes visible.
   *
   * @param slotName - The name of the ad slot.
   * @param gptWrapper - The GPT wrapper.
   * @param adNodeManager - The ad slot manager.
   */
  private refreshOrAddListener(adNode: AdNode, adEngine: AdEngine) {
    if (!this.active) return;

    if (adNode.isInViewport()) {
      adEngine.requestRender([adNode]);
    } else {
      this.refreshWhenBackInViewport(adNode, adEngine);
    }
  }

  private scheduleRefresh(slot: googletag.Slot, adEngine: AdEngine) {
    const slotName = slot.getSlotElementId() as SlotName;
    const adNode = adEngine.adNodeManager.getAdNodeByName(slotName);

    invariant(
      adNode,
      `Cannot schedule refresh. AdNode for slot ${slotName} not found`,
    );

    setTimeout(() => {
      if (adNode.isRefreshable()) {
        this.refreshOrAddListener(adNode, adEngine);
      }
    }, this.config.icRefreshTimeoutMS ?? 30_000);
  }

  hooks: Hooks = {
    /**
     * Called when the GPT wrapper is ready. It adds an event listener to the
     * `impressionViewable` event to refresh ad after a certain time.
     *
     * The plugin also requests bids from Prebid before refreshing the ad slot.
     *
     * @param gptWrapper - The GPT wrapper.
     * @param adNodeManager - The ad slot manager.
     *
     * @returns The GPT wrapper.
     */
    onAdEngineInit: (adEngine: AdEngine) => {
      this.adEngine = adEngine;
      adEngine.gptWrapper.addEventListener("slotRenderEnded", () => {});

      // Temp disabling refreshing on empty slots. Leaving the listener callback though as that
      // is less invasive with the tests and we need to get this out.
      /*
      (event: googletag.events.SlotRenderEndedEvent) => {
        // Only schedule refresh when the slot is empty. Otherwise, we will use
        // impressionViewable event to refresh the ad slot once the slot is triggered
        // as viewable.
        if (event.isEmpty) {
          this.scheduleRefresh(event.slot, adEngine);
        }
      );
      */

      adEngine.gptWrapper.addEventListener(
        "impressionViewable",
        (event: googletag.events.ImpressionViewableEvent) => {
          // Schedule a refresh for the slot when it becomes viewable.
          this.scheduleRefresh(event.slot, adEngine);
        },
      );
    },
  };

  api: SlotRefresherPluginApi = {
    stop: () => {
      this.active = false;
    },
    stopSlot: (slotName: SlotName) => {
      const adNode = this.adEngine?.adNodeManager.getAdNodeByName(slotName);
      invariant(adNode, `AdNode for slot ${slotName} not found`);
      adNode.block();
    },
  };
}
